<template>
  <div class="hp">
    <!-- Герой: приветствие, пульс компании, живые цифры, глобус -->
    <section class="hero">
      <div class="hero__bg" aria-hidden="true">
        <span class="hero__orb hero__orb--1"></span>
        <span class="hero__orb hero__orb--2"></span>
        <span class="hero__grid"></span>
        <svg class="hero__routes" viewBox="0 0 1200 520" preserveAspectRatio="none">
          <path d="M-20 420 C 260 300, 420 470, 700 330 S 1100 160, 1240 220" />
          <path d="M-20 300 C 200 200, 520 330, 760 210 S 1080 80, 1240 120" />
          <path d="M-20 500 C 300 430, 640 520, 900 400 S 1150 330, 1240 360" />
        </svg>
      </div>

      <div class="hero__main">
        <AltLogo class="hero__logo" />
        <p class="hero__date">{{ clock.date }} · <b>{{ clock.time }}</b> по Ульяновску</p>
        <h1 class="hero__title">
          <span v-for="(ch, i) in titleChars" :key="i" class="hero__ch" :style="{ '--i': i }">{{ ch === ' ' ? ' ' : ch }}</span>
        </h1>

        <template v-if="!isGuest">
          <p class="hero__pulse" :class="'is-' + pulseInfo.state">
            <span class="hero__lamp" aria-hidden="true"><i></i></span>
            {{ pulseInfo.text }}
          </p>

          <div class="stats">
            <router-link
              v-for="(s, i) in stats"
              :key="s.key"
              :to="s.to"
              class="stat"
              :class="'stat--' + s.tone"
              :style="{ '--i': i }"
              :title="s.help"
            >
              <span class="stat__value">{{ s.shown }}</span>
              <span class="stat__label">{{ s.label }}</span>
              <span class="stat__help">{{ s.help }}</span>
            </router-link>
          </div>
        </template>

        <template v-else>
          <p class="hero__lead">Единая площадка ALT: рейсы, исполнители, документы и деньги — в одном месте.</p>
          <router-link to="/login" class="hero__cta">Войти в систему <font-awesome-icon icon="fa-solid fa-arrow-right" /></router-link>
        </template>
      </div>

      <div v-if="!isGuest" class="hero__globe">
        <TripsGlobe scope="all" :dark="true" />
      </div>
    </section>

    <div v-if="!isGuest" class="board">
      <!-- Что горит -->
      <section class="card card--wide" :style="{ '--i': 0 }">
        <header class="card__head">
          <div>
            <h2>Что горит прямо сейчас</h2>
            <p>Рейсы, где каждый час промедления стоит денег. Нажмите — откроется «Сводка логиста».</p>
          </div>
          <div class="lamps">
            <router-link v-for="b in boards" :key="b.key" to="/summary" class="lamp" :class="'lamp--' + b.state" :title="b.text">
              <i aria-hidden="true"></i>{{ b.title || b.fallback }}
            </router-link>
          </div>
        </header>

        <div v-if="loading && !summary" class="skeleton"><span v-for="n in 4" :key="n"></span></div>
        <ul v-else-if="fires.length" class="fires">
          <li v-for="(f, i) in fires" :key="f.key" :style="{ '--i': i }">
            <router-link to="/summary" class="fire" :class="'fire--' + f.level">
              <span class="fire__count">{{ f.count }}</span>
              <span class="fire__text">
                <b>{{ f.title }}</b>
                <span>{{ f.hint }}</span>
              </span>
              <font-awesome-icon class="fire__go" icon="fa-solid fa-arrow-right" />
            </router-link>
          </li>
        </ul>
        <div v-else class="calm">
          <span class="calm__icon">✓</span>
          <div><b>Ничего не горит.</b> Все рейсы идут по плану — система следит сама и подсветит, если что-то пойдёт не так.</div>
        </div>
      </section>

      <!-- Претензионная работа -->
      <section class="card" :style="{ '--i': 1 }">
        <header class="card__head">
          <div>
            <h2>Деньги, которые возвращаем</h2>
            <p>Срывы, отказы и простои за {{ claimsDays }} дней.</p>
          </div>
        </header>
        <router-link to="/claims" class="money">
          <span class="money__value">{{ moneyShown }}</span>
          <span class="money__label">к выставлению по претензиям</span>
        </router-link>
        <ul class="claims">
          <li v-for="c in claimsList" :key="c.key">
            <router-link to="/claims" class="claim" :class="'claim--' + c.level">
              <span class="claim__dot" aria-hidden="true"></span>
              <span class="claim__title">{{ c.title }}</span>
              <b>{{ c.count }}</b>
            </router-link>
          </li>
          <li v-if="!claimsList.length && claims" class="claims__empty">Сейчас ничего не ждёт претензии.</li>
        </ul>
      </section>

      <!-- Быстрый вход -->
      <section class="card card--full" :style="{ '--i': 2 }">
        <header class="card__head">
          <div>
            <h2>Куда пойти</h2>
            <p>Ваши главные разделы. Меню слева запоминает, чем вы пользуетесь, и выделяет частое жирным.</p>
          </div>
          <router-link class="card__link" :to="{ path: '/knowledge', query: { a: 'menu-and-home' } }">Как устроено меню</router-link>
        </header>
        <div class="tiles">
          <router-link
            v-for="(t, i) in tiles"
            :key="t.key"
            :to="t.path"
            class="tile"
            :class="{ 'tile--hot': t.hot }"
            :style="{ '--i': i }"
          >
            <span class="tile__icon"><font-awesome-icon :icon="['fa-solid', t.icon]" /></span>
            <span class="tile__name">{{ t.name }}</span>
            <span class="tile__hint">{{ t.hint }}</span>
            <font-awesome-icon class="tile__go" icon="fa-solid fa-arrow-right" />
          </router-link>
        </div>
      </section>

      <footer class="foot">
        <router-link :to="{ path: '/knowledge', query: { a: 'whats-new' } }">🆕 Что нового в системе</router-link>
        <span>·</span>
        <router-link :to="{ path: '/knowledge', query: { a: 'traffic-lights' } }">Что значат цвета</router-link>
        <span>·</span>
        <span class="foot__upd">Обновлено в {{ updatedAt || '—' }}, дальше — само каждые 2 минуты</span>
      </footer>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import '@/navigation/icons'
import AltLogo from '@/components/workspace/AltLogo.vue'
import TripsGlobe from '@/components/workspace/TripsGlobe.vue'
import { fetchWorkspaceLights, fetchWorkspaceSummary } from '@/backend/workspaceApi'
import { fetchClaimsSummary } from '@/backend/claimsApi'
import { buildMenu } from '@/navigation/menuLogic.mjs'
import {
  boardState, claimsMoney, claimsTop, easeOut, firstName, greeting, hotGroups, money, officeParts, pulse,
} from '@/pages/home/homeLogic.mjs'

const REFRESH_MS = 2 * 60 * 1000
const COUNT_MS = 1400

export default {
  name: 'HomePage',
  components: { AltLogo, TripsGlobe },
  setup() {
    const store = useStore()
    const user = computed(() => store.state.user.user)
    const isGuest = computed(() => !user.value?.role?.name || user.value.role.name === 'Гость')

    // Часы офиса
    const now = ref(new Date())
    const clock = computed(() => officeParts(now.value))
    const titleChars = computed(() => {
      if (isGuest.value) return Array.from('Добро пожаловать в ALT GROUP')
      const name = firstName(user.value?.name)
      return Array.from(greeting(clock.value.hour) + (name ? ', ' + name : ''))
    })

    // Данные
    const summary = ref(null)
    const lights = ref(null)
    const claims = ref(null)
    const loading = ref(false)
    const updatedAt = ref('')

    async function load() {
      if (isGuest.value || document.hidden) return
      loading.value = true
      const [s, l, c] = await Promise.allSettled([
        fetchWorkspaceSummary('all'), fetchWorkspaceLights('all'), fetchClaimsSummary(),
      ])
      if (s.status === 'fulfilled') summary.value = s.value
      if (l.status === 'fulfilled') lights.value = l.value
      if (c.status === 'fulfilled') claims.value = c.value
      updatedAt.value = officeParts(new Date()).time
      loading.value = false
    }

    const totals = computed(() => summary.value?.totals || null)
    const pulseInfo = computed(() => pulse(lights.value, totals.value))
    const fires = computed(() => hotGroups(summary.value?.groups || []))
    const boards = computed(() => [
      { key: 'system', fallback: 'Техническое состояние', ...boardState(lights.value, 'system') },
      { key: 'logist', fallback: 'Светофор логиста', ...boardState(lights.value, 'logist') },
    ])
    const claimsList = computed(() => claimsTop(claims.value?.segments || []))
    const claimsDays = computed(() => claims.value?.days ?? 7)

    // Живые цифры с плавным счётом
    const targets = computed(() => ({
      active: totals.value?.active, in_transit: totals.value?.in_transit,
      needs_executor: totals.value?.needs_executor, critical: totals.value?.critical,
      money: claimsMoney(claims.value?.segments || []),
    }))
    const shown = ref({ active: 0, in_transit: 0, needs_executor: 0, critical: 0, money: 0 })
    let raf = null
    let settle = null
    const finalOf = (to, from) => {
      const next = {}
      for (const key of Object.keys(from)) {
        const target = Number(to[key])
        next[key] = Number.isFinite(target) ? target : from[key]
      }
      return next
    }
    function animateTo(to) {
      cancelAnimationFrame(raf)
      clearTimeout(settle)
      const from = { ...shown.value }
      // Вкладка в фоне не проигрывает анимацию — сразу настоящие числа, без «0 критичных».
      if (document.hidden) { shown.value = finalOf(to, from); return }
      settle = setTimeout(() => { cancelAnimationFrame(raf); shown.value = finalOf(to, from) }, COUNT_MS + 150)
      const start = performance.now()
      const step = (t) => {
        const k = easeOut(Math.min(1, (t - start) / COUNT_MS))
        const next = {}
        for (const key of Object.keys(from)) {
          const target = Number(to[key])
          next[key] = Number.isFinite(target) ? Math.round(from[key] + (target - from[key]) * k) : from[key]
        }
        shown.value = next
        if (k < 1) raf = requestAnimationFrame(step)
      }
      raf = requestAnimationFrame(step)
    }
    watch(targets, (to) => animateTo(to))

    const dash = (key) => (totals.value ? shown.value[key] : '—')
    const stats = computed(() => [
      { key: 'active', label: 'рейсов в работе', tone: 'gold', to: '/trips', shown: dash('active'),
        help: 'Все незакрытые рейсы компании' },
      { key: 'in_transit', label: 'в пути', tone: 'blue', to: '/summary', shown: dash('in_transit'),
        help: 'Машины едут к выгрузке — они на глобусе справа' },
      { key: 'needs_executor', label: 'ждут исполнителя', tone: (totals.value?.needs_executor || 0) > 0 ? 'amber' : 'green',
        to: '/summary', shown: dash('needs_executor'), help: 'Рейсы без реального исполнителя' },
      { key: 'critical', label: 'критичных', tone: (totals.value?.critical || 0) > 0 ? 'red' : 'green',
        to: '/summary', shown: dash('critical'), help: 'Риск срыва: нужно действовать сейчас' },
    ])
    const moneyShown = computed(() => (claims.value ? money(shown.value.money) : '—'))

    // Быстрый вход — те же разделы «Каждый день», что в меню слева (без «Главной»).
    const tiles = computed(() => {
      if (isGuest.value) return []
      const menu = buildMenu({
        role: user.value?.role?.name, isGuest: false,
        hasPermission: (p) => store.getters.hasPermission(p), dynamic: {},
      })
      return [...menu.daily, ...menu.footer].filter((i) => i.key !== 'home').slice(0, 8)
    })

    let tick = null
    let timer = null
    const onVisible = () => { if (!document.hidden) load() }
    onMounted(() => {
      tick = setInterval(() => { now.value = new Date() }, 15 * 1000)
      load()
      timer = setInterval(load, REFRESH_MS)
      document.addEventListener('visibilitychange', onVisible)
    })
    watch(isGuest, (guest) => { if (!guest) load() })
    onBeforeUnmount(() => {
      clearInterval(tick)
      clearInterval(timer)
      cancelAnimationFrame(raf)
      clearTimeout(settle)
      document.removeEventListener('visibilitychange', onVisible)
    })

    return {
      isGuest, clock, titleChars, pulseInfo, stats, summary, loading, fires, boards,
      claims, claimsList, claimsDays, moneyShown, tiles, updatedAt,
    }
  },
}
</script>

<style scoped>
.hp {
  --ink: #1f2430; --muted: #6b7180; --line: #ebe7dd; --paper: #f7f6f2;
  --gold: #d9b65a; --gold-2: #b8923a; --gold-soft: #fbf4df;
  min-height: 100%; background: var(--paper); color: var(--ink);
  font-family: Inter, 'Segoe UI', Roboto, system-ui, -apple-system, sans-serif;
}

/* ---------- Герой ---------- */
.hero {
  position: relative; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); gap: 24px;
  padding: 34px 40px 40px; min-height: 470px; overflow: hidden; color: #eef0f4;
  background: radial-gradient(120% 140% at 0% 0%, #232838 0%, #141821 55%, #0f1218 100%);
  isolation: isolate;
}
.hero__bg { position: absolute; inset: 0; z-index: -1; pointer-events: none; }
.hero__orb { position: absolute; border-radius: 50%; filter: blur(60px); opacity: .55; }
.hero__orb--1 { width: 460px; height: 460px; left: -120px; top: -160px; background: rgba(217, 182, 90, .35); animation: hp-orb 16s ease-in-out infinite alternate; }
.hero__orb--2 { width: 520px; height: 520px; right: -160px; bottom: -260px; background: rgba(82, 115, 230, .25); animation: hp-orb 20s ease-in-out infinite alternate-reverse; }
.hero__grid {
  position: absolute; inset: 0; opacity: .35;
  background-image: linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 44px 44px; mask-image: radial-gradient(70% 80% at 30% 30%, #000, transparent);
}
.hero__routes { position: absolute; inset: 0; width: 100%; height: 100%; opacity: .5; }
.hero__routes path {
  fill: none; stroke: rgba(226, 196, 110, .55); stroke-width: 1.4; stroke-dasharray: 6 14;
  animation: hp-dash 22s linear infinite;
}
.hero__routes path:nth-child(2) { stroke: rgba(255, 255, 255, .25); animation-duration: 30s; }
.hero__routes path:nth-child(3) { stroke: rgba(120, 150, 255, .35); animation-duration: 26s; animation-direction: reverse; }

.hero__main { display: flex; flex-direction: column; justify-content: center; gap: 14px; min-width: 0; }
.hero__logo { transform-origin: left center; }
.hero__date { margin: 6px 0 0; font-size: 13px; letter-spacing: .02em; color: #9ba2b2; text-transform: capitalize; animation: hp-fade .8s ease .5s backwards; }
.hero__date b { color: #fff; font-weight: 600; }
.hero__title { margin: 0; font-size: clamp(30px, 3.4vw, 46px); line-height: 1.05; font-weight: 800; letter-spacing: -.02em; }
.hero__ch {
  display: inline-block; animation: hp-ch .7s cubic-bezier(.2, .9, .3, 1.2) backwards; animation-delay: calc(.35s + var(--i) * 28ms);
  background: linear-gradient(100deg, #fff 0%, #fff 55%, #f0d27c 75%, #fff 95%); background-size: 300% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.hero__pulse {
  display: inline-flex; align-items: center; gap: 12px; align-self: flex-start; margin: 4px 0 6px; padding: 10px 16px 10px 12px;
  border-radius: 999px; font-size: 15px; font-weight: 500; color: #fff;
  background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .1); backdrop-filter: blur(6px);
  animation: hp-fade .8s ease 1s backwards;
}
.hero__lamp { position: relative; width: 14px; height: 14px; }
.hero__lamp i { position: absolute; inset: 0; border-radius: 50%; background: var(--c, #9ca3af); box-shadow: 0 0 14px var(--c, #9ca3af); }
.hero__lamp::after { content: ''; position: absolute; inset: -6px; border-radius: 50%; border: 2px solid var(--c, #9ca3af); opacity: 0; animation: hp-ring 2s ease-out infinite; }
.hero__pulse.is-green { --c: #34d399; } .hero__pulse.is-yellow { --c: #fbbf24; } .hero__pulse.is-red { --c: #f87171; } .hero__pulse.is-gray { --c: #9ca3af; }
.hero__lead { max-width: 520px; margin: 0; font-size: 17px; line-height: 1.5; color: #c3c8d3; }
.hero__cta {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 10px; padding: 14px 22px; border-radius: 14px;
  font-weight: 700; color: #1a1d25; text-decoration: none; background: linear-gradient(135deg, #f0d27c, #c9a24a);
  box-shadow: 0 14px 30px -12px rgba(217, 182, 90, .8); transition: transform .2s, box-shadow .2s;
}
.hero__cta:hover { transform: translateY(-2px); box-shadow: 0 20px 36px -12px rgba(217, 182, 90, .9); }

.stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 6px; }
.stat {
  --c: #e2c46e; position: relative; display: flex; flex-direction: column; gap: 4px; padding: 16px 16px 14px; border-radius: 18px;
  color: #fff; text-decoration: none; overflow: hidden;
  background: linear-gradient(160deg, rgba(255,255,255,.09), rgba(255,255,255,.03)); border: 1px solid rgba(255,255,255,.09);
  transition: transform .3s cubic-bezier(.2, .8, .2, 1), border-color .3s, box-shadow .3s;
  animation: hp-rise .7s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(1.05s + var(--i) * 90ms);
}
.stat::before { content: ''; position: absolute; left: 0; top: 0; right: 0; height: 3px; background: var(--c); opacity: .9; }
.stat::after {
  content: ''; position: absolute; inset: auto -30% -60% auto; width: 140px; height: 140px; border-radius: 50%;
  background: var(--c); opacity: .14; filter: blur(30px); transition: opacity .3s;
}
.stat:hover { transform: translateY(-4px); border-color: color-mix(in srgb, var(--c) 60%, transparent); box-shadow: 0 18px 40px -20px var(--c); }
.stat:hover::after { opacity: .3; }
.stat--gold { --c: #e2c46e; } .stat--blue { --c: #7c9cff; } .stat--amber { --c: #fbbf24; } .stat--red { --c: #f87171; } .stat--green { --c: #34d399; }
.stat__value { font-size: 38px; font-weight: 800; line-height: 1; letter-spacing: -.02em; font-variant-numeric: tabular-nums; }
.stat--red .stat__value { color: #ffb4b4; }
.stat__label { font-size: 13px; color: #c3c8d3; }
.stat__help { font-size: 11.5px; line-height: 1.3; color: #8e95a5; max-height: 0; opacity: 0; transition: max-height .3s, opacity .3s; }
.stat:hover .stat__help { max-height: 40px; opacity: 1; }

.hero__globe {
  position: relative; min-width: 0; border-radius: 24px; overflow: hidden;
  background: radial-gradient(60% 60% at 50% 45%, rgba(124, 156, 255, .12), transparent 70%);
  animation: hp-fade 1.2s ease .6s backwards;
}
.hero__globe :deep(.tg) { background: transparent; border: 0; box-shadow: none; }

/* ---------- Доска ---------- */
.board {
  display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 20px;
  max-width: 1600px; margin: -36px auto 0; padding: 0 40px 36px; position: relative;
}
.card {
  position: relative; padding: 22px 22px 20px; border-radius: 22px; background: #fff; border: 1px solid var(--line);
  box-shadow: 0 24px 50px -36px rgba(31, 36, 48, .45);
  animation: hp-rise .7s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(.9s + var(--i) * 120ms);
}
.card--full { grid-column: 1 / -1; }
.card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.card__head h2 { margin: 0 0 4px; font-size: 19px; font-weight: 800; letter-spacing: -.01em; }
.card__head p { margin: 0; font-size: 13px; line-height: 1.45; color: var(--muted); }
.card__link { flex: 0 0 auto; font-size: 13px; font-weight: 600; color: var(--gold-2); text-decoration: none; border-bottom: 1px dashed currentColor; }

.lamps { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }
.lamp {
  --c: #9ca3af; display: inline-flex; align-items: center; gap: 8px; padding: 7px 12px; border-radius: 999px; font-size: 12.5px;
  font-weight: 600; color: var(--ink); text-decoration: none; background: color-mix(in srgb, var(--c) 10%, #fff); border: 1px solid color-mix(in srgb, var(--c) 30%, #fff);
}
.lamp i { width: 9px; height: 9px; border-radius: 50%; background: var(--c); box-shadow: 0 0 0 3px color-mix(in srgb, var(--c) 22%, transparent); }
.lamp--green { --c: #16a34a; } .lamp--yellow { --c: #d97706; } .lamp--red { --c: #dc2626; }
.lamp--red i, .lamp--yellow i { animation: hp-blink 1.6s ease-in-out infinite; }

.fires { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
.fires li { animation: hp-rise .6s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(1.1s + var(--i) * 80ms); }
.fire {
  --c: #d97706; display: flex; align-items: center; gap: 16px; padding: 14px 16px; border-radius: 16px; text-decoration: none; color: var(--ink);
  background: linear-gradient(90deg, color-mix(in srgb, var(--c) 9%, #fff), #fff 70%); border: 1px solid color-mix(in srgb, var(--c) 22%, #fff);
  transition: transform .25s, box-shadow .25s;
}
.fire--critical { --c: #dc2626; }
.fire:hover { transform: translateX(4px); box-shadow: 0 12px 28px -18px var(--c); }
.fire__count {
  flex: 0 0 auto; display: grid; place-items: center; min-width: 52px; height: 52px; padding: 0 10px; border-radius: 14px;
  font-size: 22px; font-weight: 800; color: #fff; background: var(--c); box-shadow: 0 10px 20px -10px var(--c);
}
.fire__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.fire__text b { font-size: 14.5px; }
.fire__text span { font-size: 12.5px; line-height: 1.4; color: var(--muted); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.fire__go { color: var(--c); opacity: .6; transition: transform .25s, opacity .25s; }
.fire:hover .fire__go { transform: translateX(4px); opacity: 1; }

.calm { display: flex; align-items: center; gap: 14px; padding: 18px; border-radius: 16px; background: #f0fdf4; color: #14532d; font-size: 14px; line-height: 1.5; }
.calm__icon { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: #16a34a; color: #fff; font-weight: 800; }

.money {
  display: flex; flex-direction: column; gap: 4px; padding: 18px; border-radius: 18px; text-decoration: none; color: var(--ink);
  background: linear-gradient(135deg, var(--gold-soft), #fff 80%); border: 1px solid #f1e3b6; transition: transform .25s, box-shadow .25s;
}
.money:hover { transform: translateY(-2px); box-shadow: 0 16px 30px -22px rgba(184, 146, 58, .9); }
.money__value { font-size: 32px; font-weight: 800; letter-spacing: -.02em; font-variant-numeric: tabular-nums; color: #8a6a1f; }
.money__label { font-size: 12.5px; color: var(--muted); }
.claims { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 6px; }
.claim { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 12px; text-decoration: none; color: var(--ink); font-size: 13px; transition: background .2s; }
.claim:hover { background: #f6f4ee; }
.claim__dot { width: 8px; height: 8px; border-radius: 50%; background: #d97706; flex: 0 0 auto; }
.claim--critical .claim__dot { background: #dc2626; }
.claim__title { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.claims__empty { font-size: 13px; color: var(--muted); padding: 8px 10px; }

.tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 12px; }
.tile {
  position: relative; display: flex; flex-direction: column; gap: 6px; padding: 16px 16px 18px; border-radius: 18px; text-decoration: none;
  color: var(--ink); background: #fbfaf7; border: 1px solid var(--line); overflow: hidden;
  transition: transform .3s cubic-bezier(.2, .8, .2, 1), box-shadow .3s, border-color .3s, background .3s;
  animation: hp-rise .6s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(1.3s + var(--i) * 60ms);
}
.tile::after {
  content: ''; position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
  background: linear-gradient(120deg, transparent 30%, rgba(240, 210, 124, .35) 50%, transparent 70%); background-size: 250% 100%;
  background-position: 120% 0; transition: background-position .8s ease;
}
.tile:hover { transform: translateY(-4px); background: #fff; border-color: #ead9a6; box-shadow: 0 22px 40px -28px rgba(31, 36, 48, .55); }
.tile:hover::after { background-position: -40% 0; }
.tile__icon {
  display: grid; place-items: center; width: 42px; height: 42px; border-radius: 13px; font-size: 17px; color: #fff;
  background: linear-gradient(135deg, #2a3040, #171a22); box-shadow: 0 10px 20px -12px rgba(23, 26, 34, .8); transition: transform .3s;
}
.tile--hot .tile__icon { color: #1a1d25; background: linear-gradient(135deg, #f0d27c, #c9a24a); box-shadow: 0 10px 22px -12px rgba(217, 182, 90, .9); }
.tile:hover .tile__icon { transform: rotate(-6deg) scale(1.06); }
.tile__name { font-size: 15px; font-weight: 700; margin-top: 4px; }
.tile__hint { font-size: 12.5px; line-height: 1.4; color: var(--muted); }
.tile__go { position: absolute; right: 16px; top: 18px; color: #c9b27a; opacity: 0; transform: translateX(-6px); transition: opacity .25s, transform .25s; }
.tile:hover .tile__go { opacity: 1; transform: none; }

.skeleton { display: grid; gap: 10px; }
.skeleton span { height: 70px; border-radius: 16px; background: linear-gradient(90deg, #f1efe9, #faf9f6, #f1efe9); background-size: 200% 100%; animation: hp-shimmer 1.4s linear infinite; }

.foot { grid-column: 1 / -1; display: flex; flex-wrap: wrap; align-items: center; gap: 10px; justify-content: center; padding-top: 4px; font-size: 12.5px; color: var(--muted); }
.foot a { color: var(--gold-2); text-decoration: none; font-weight: 600; }
.foot a:hover { text-decoration: underline; }

@keyframes hp-ch { from { opacity: 0; transform: translateY(18px) skewX(-10deg); } }
@keyframes hp-fade { from { opacity: 0; } }
@keyframes hp-rise { from { opacity: 0; transform: translateY(18px); } }
@keyframes hp-orb { to { transform: translate3d(60px, 40px, 0) scale(1.1); } }
@keyframes hp-dash { to { stroke-dashoffset: -800; } }
@keyframes hp-ring { 0% { opacity: .7; transform: scale(.6); } 100% { opacity: 0; transform: scale(1.6); } }
@keyframes hp-blink { 50% { opacity: .35; } }
@keyframes hp-shimmer { to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) {
  .hp *, .hp *::before, .hp *::after { animation: none !important; transition: none !important; }
}

@media (max-width: 1280px) {
  .hero { grid-template-columns: 1fr; }
  .hero__globe { height: 380px; }
  .stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 900px) {
  .board { grid-template-columns: 1fr; padding: 0 16px 28px; }
  .hero { padding: 26px 16px 50px; }
  .card__head { flex-direction: column; }
  .lamps { justify-content: flex-start; }
}
</style>
